import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import root from 'window-or-global';
import cx from 'classnames';

import styles from './ProgressBar.pcss';

/* Draggable progress bar supporting up to two sliders:
   - Primary slider (required)
        can be updated user (ex: video progress)
   - Second slider
        cannot be updated by user (ex: loading progress) */
class ProgressBar extends PureComponent {
    static propTypes = {
        backgroundColor: PropTypes.string,
        onUpdate: PropTypes.func.isRequired,
        onToggleDragging: PropTypes.func,
        primarySliderLabel: PropTypes.string.isRequired,
        primarySliderValue: PropTypes.number.isRequired,
        primaryTotal: PropTypes.number.isRequired,
        secondarySliderLabel: PropTypes.string,
        secondarySliderValue: PropTypes.number,
        secondaryTotal: PropTypes.number,
        shouldHideIndicator: PropTypes.bool
    };

    static defaultProps = {
        shouldHideIndicator: false
    };

    updatePrimarySliderValue = e => {
        const {primaryTotal, onUpdate} = this.props;

        let newValue;

        const progressRect = this.wrapper.getBoundingClientRect();
        const diff = e.clientX - progressRect.left;

        if (diff > progressRect.width) {
            newValue = primaryTotal;
        } else if (diff < 0) {
            newValue = 0;
        } else {
            newValue = primaryTotal * (diff / progressRect.width);
        }

        onUpdate(newValue);
    };

    onDragStart = () => {
        const {onToggleDragging} = this.props;

        if (onToggleDragging) onToggleDragging(true);

        root.addEventListener('mousemove', this.updatePrimarySliderValue);
        root.addEventListener('mouseup', this.onDragEnd);
    };

    onDragEnd = () => {
        const {onToggleDragging} = this.props;

        if (onToggleDragging) onToggleDragging(false);

        root.removeEventListener('mousemove', this.updatePrimarySliderValue);
        root.removeEventListener('mouseup', this.onDragEnd);
    };

    render() {
        const {
            backgroundColor,
            primarySliderLabel,
            primarySliderValue,
            primaryTotal,
            secondarySliderLabel,
            secondarySliderValue,
            secondaryTotal,
            shouldHideIndicator
        } = this.props;

        const primaryPercentage = `${100 * (primarySliderValue / primaryTotal)}%`;

        return (
            <span
                className={cx(styles.wrapper, {
                    [styles.shouldHideIndicator]: shouldHideIndicator
                })}>
                <span className={styles.inner}>
                    <button
                        aria-hidden={true}
                        className={styles.button}
                        onClick={this.updatePrimarySliderValue}
                        ref={el => {
                            this.wrapper = el;
                        }}
                    />

                    <span
                        aria-label={primarySliderLabel}
                        aria-valuemin="0"
                        aria-valuemax={primaryTotal}
                        aria-valuenow={primarySliderValue}
                        className={cx(styles.rail, styles.primary)}
                        style={{
                            backgroundColor,
                            width: primaryPercentage
                        }}
                        role="slider"
                    />
                    <span
                        aria-hidden={true}
                        className={styles.primaryIndicator}
                        onMouseDown={this.onDragStart}
                        style={{
                            left: primaryPercentage
                        }}
                    />

                    {secondarySliderLabel && (
                        <span
                            aria-label={secondarySliderLabel}
                            aria-valuemin="0"
                            aria-valuemax={secondaryTotal}
                            aria-valuenow={secondarySliderValue}
                            className={cx(styles.rail, styles.secondary)}
                            style={{
                                backgroundColor,
                                width: `${100 * (secondarySliderValue / secondaryTotal)}%`
                            }}
                            role="slider"
                        />
                    )}

                    <span className={cx(styles.rail, styles.background)} />
                </span>
            </span>
        );
    }
}

export default ProgressBar;
